import * as Sentry from "@sentry/browser";

export type BrowserScreen = "discover" | "signup" | "login" | "verification" | "lobby" | "game" | "results";
export type BrowserMetric =
  | { kind: "view"; screen: BrowserScreen }
  | { kind: "room-request"; action: "create" | "join" | "resume" }
  | { kind: "campaign-selected" }
  | { kind: "canvas-load"; outcome: "ready" | "failed" }
  | { kind: "auth-submitted"; method: "signup" | "login" }
  | { kind: "auth-response"; method: "signup" | "login"; outcome: "accepted" | "failed" | "verification-required" };

const allowedAttributes = new Set(["screen", "action", "outcome", "service", "sentry.environment",
  "sentry.release", "sentry.sdk.name", "sentry.sdk.version", "sentry.timestamp.sequence"]);
const names = new Set(["ui.views", "ui.room_requests", "ui.campaign_selections", "ui.canvas_loads",
  "ui.signup_submissions", "ui.signup_responses", "ui.login_submissions", "ui.login_responses"]);

/** Browser-only entry point: no Node SDK, identity, storage, or automatic capture. */
export function createBrowserMetrics(options: { enabled?: boolean; dsn?: string; environment?: string; release?: string }) {
  let client: ReturnType<typeof Sentry.init>;
  let closed = false;
  if (options.enabled) {
    try {
      const dsn = new URL(options.dsn ?? "");
      if (!['http:', 'https:'].includes(dsn.protocol) || !dsn.username || !/\/\d+$/.test(dsn.pathname) ||
          dsn.password || dsn.search || dsn.hash || !options.environment ||
          options.environment.length > 64 || /\s/.test(options.environment)) throw new Error("Invalid metrics configuration");
      client = Sentry.init({
        dsn: dsn.href,
        environment: options.environment,
        release: options.release || undefined,
        defaultIntegrations: false,
        sendDefaultPii: false,
        sendClientReports: false,
        enableLogs: false,
        enableMetrics: true,
        beforeSend: () => null,
        beforeSendTransaction: () => null,
        beforeSendMetric: (metric) => names.has(metric.name) ? metric : null,
      });
      // The SDK merges scope attributes after beforeSendMetric. Strip enrichment
      // from the actual envelope, including inherited user, URL, and replay data.
      client?.on("beforeEnvelope", (envelope) => {
        for (const [header, payload] of envelope[1]) {
          if (header.type !== "trace_metric") continue;
          const container = payload as { items: Array<{ attributes?: Record<string, unknown> }> };
          for (const metric of container.items) {
            metric.attributes = Object.fromEntries(Object.entries(metric.attributes ?? {})
              .filter(([key]) => allowedAttributes.has(key)));
          }
        }
      });
    } catch {
      console.warn("Browser metrics disabled: invalid configuration or initialization failure.");
    }
  }
  return {
    observe(event: BrowserMetric): void {
      if (!client || closed) return;
      try {
        const metric = browserMetric(event);
        if (!metric) return;
        Sentry.withScope(new Sentry.Scope(), (scope) => {
          scope.setClient(client);
          Sentry.metrics.count(metric.name, 1, { attributes: { ...metric.attributes, service: "web" } });
        });
      } catch {
        // Tracking cannot break navigation, authentication, or play.
      }
    },
    async flush(): Promise<void> {
      try { await client?.flush(2_000); } catch { /* Best effort, including page exit. */ }
    },
    async close(): Promise<void> {
      if (closed) return;
      closed = true;
      try { await client?.close(2_000); } catch { /* Delivery never blocks the UI. */ }
    },
  };
}

function browserMetric(event: BrowserMetric): { name: string; attributes: Record<string, string> } | undefined {
  switch (event.kind) {
    case "view":
      if (!["discover", "signup", "login", "verification", "lobby", "game", "results"].includes(event.screen)) return;
      return { name: "ui.views", attributes: { screen: event.screen } };
    case "room-request":
      if (!["create", "join", "resume"].includes(event.action)) return;
      return { name: "ui.room_requests", attributes: { action: event.action } };
    case "campaign-selected":
      return { name: "ui.campaign_selections", attributes: {} };
    case "canvas-load":
      if (!["ready", "failed"].includes(event.outcome)) return;
      return { name: "ui.canvas_loads", attributes: { outcome: event.outcome } };
    case "auth-submitted":
      if (!["signup", "login"].includes(event.method)) return;
      return { name: `ui.${event.method}_submissions`, attributes: {} };
    case "auth-response":
      if (!["signup", "login"].includes(event.method) ||
          !["accepted", "failed", "verification-required"].includes(event.outcome)) return;
      return { name: `ui.${event.method}_responses`, attributes: { outcome: event.outcome } };
  }
}



let metrics: ReturnType<typeof createBrowserMetrics> | undefined;

export function observeBrowserMetric(event: BrowserMetric): void {
  metrics?.observe(event);
}

export function initializeBrowserMetrics(): void {
  if (metrics) return;
  metrics = createBrowserMetrics({
    enabled: import.meta.env.VITE_SENTRY_METRICS_ENABLED === "true",
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    release: import.meta.env.VITE_SENTRY_RELEASE,
  });
  const flush = () => { void metrics?.flush(); };
  const onVisibilityChange = () => { if (document.visibilityState === "hidden") flush(); };
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", onVisibilityChange);
  import.meta.hot?.dispose(() => {
    window.removeEventListener("pagehide", flush);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    void metrics?.close();
    metrics = undefined;
  });
}
